import { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";

import { PlusIcon, Trash2Icon } from "lucide-react";

import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { Icon } from "simplydo/react/ui/icon";
import { Loader } from "simplydo/react/ui/loader";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import api from "api";

import AchievementComponent from "components/lib/Users/Achievement";

import type {
  AchievementAwarderProps,
  AwardedCustomAchievement,
  OrganisationAchievement,
} from "./types";

export const AchievementContainer = styled.div`
  display: flex;
  flex-direction: column;
  .achievement-component {
    width: 100%;
    margin: 0;
  }
  .ui.button {
    margin: 0 !important;
  }
`;

const AchievementAwarder = ({
  user,
  targetUser,
  onClose,
}: AchievementAwarderProps) => {
  const [achievements, setAchievements] = useState<OrganisationAchievement[]>(
    [],
  );
  const [achievementsLoading, setAchievementsLoading] = useState(false);
  const [customAchievements, setCustomAchievements] = useState<
    AwardedCustomAchievement[]
  >([]);
  const organisationId = user?.ownerOrganisation?._id ?? "";
  const targetUserId = targetUser?._id ?? "";

  useEffect(() => {
    if (targetUserId) {
      api.users.get(
        targetUserId,
        (dataUser) => {
          setCustomAchievements(dataUser.customAchievements ?? []);
        },
        () => {},
      );
    } else {
      setCustomAchievements([]);
    }
  }, [targetUserId]);

  useEffect(() => {
    if (!organisationId || !targetUserId) return;
    setAchievementsLoading(true);
    api.organisations.getAchievements(
      organisationId,
      (data) => {
        setAchievements(data.achievements);
        setAchievementsLoading(false);
      },
      (err) => {
        toast.error(err.message);
        setAchievementsLoading(false);
      },
    );
  }, [organisationId, targetUserId]);

  const hasBeenAwarded = useCallback(
    (achievement: OrganisationAchievement) => {
      const customAchievementIds = customAchievements.map((i) => i._id);
      return customAchievementIds.indexOf(achievement._id) > -1;
    },
    [customAchievements],
  );

  const onAwardAchievement = useCallback(
    (achievement: OrganisationAchievement) => {
      api.organisations.awardAchievement(
        organisationId,
        achievement._id,
        targetUserId,
        () => {
          setCustomAchievements((prevAchievements) => [
            achievement,
            ...prevAchievements,
          ]);
        },
        (err) => toast.error(err.message),
      );
    },
    [organisationId, targetUserId],
  );

  const onRemoveAward = useCallback(
    (achievementId: string) => {
      api.organisations.removeAwardAchievement(
        organisationId,
        achievementId,
        targetUserId,
        () => {
          setCustomAchievements((prevAchievements) =>
            prevAchievements.filter((a) => a._id !== achievementId),
          );
        },
        () => {},
      );
    },
    [organisationId, targetUserId],
  );

  return (
    <Modal open={!!targetUser} onClose={onClose}>
      <ModalHeader>Award custom achievements</ModalHeader>
      <ModalContent>
        {achievementsLoading ? (
          <Loader />
        ) : (
          <Grid mobile={1} columns={16} style={{ margin: "1rem 0" }}>
            {achievements.map((achievement) => (
              <GridColumn
                stretched
                span={16}
                computer={4}
                tablet={5}
                key={achievement._id}
                style={{ textAlign: "center" }}
              >
                <AchievementContainer>
                  <AchievementComponent
                    achievement={achievement}
                    showDate
                    showAllInfo
                  />
                  {hasBeenAwarded(achievement) ? (
                    <Button
                      onClick={() => onRemoveAward(achievement._id)}
                      variant="outline"
                    >
                      <Icon icon={Trash2Icon} />
                      Remove award
                    </Button>
                  ) : (
                    <Button
                      onClick={() => onAwardAchievement(achievement)}
                      color="primary"
                    >
                      <Icon icon={PlusIcon} />
                      Award achievement
                    </Button>
                  )}
                </AchievementContainer>
              </GridColumn>
            ))}
          </Grid>
        )}
      </ModalContent>
      <ModalActions>
        <Button onClick={onClose}>Done</Button>
      </ModalActions>
    </Modal>
  );
};

export default AchievementAwarder;
